import React from 'react';
import { Calendar, Clock, MapPin, CheckCircle, Gift } from 'lucide-react';
import RSVPPopup from './RSVPPopup';

type HeroSectionProps = {
  onCtaClick: () => void;
};

const HeroSection: React.FC<HeroSectionProps> = ({ onCtaClick }) => {
  const [showRSVPPopup, setShowRSVPPopup] = React.useState(false);

  return (
    <>
      <section className="relative min-h-screen flex items-center justify-center px-4 py-12 overflow-hidden">
        {/* Background with subtle gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#fefbf4] via-[#fefbf4] to-[#f9f5ef]"></div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-[#e2c1b6] opacity-10 blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-[#e2c1b6] opacity-15 blur-3xl"></div>

        <div className="relative z-10 max-w-6xl mx-auto">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src="https://www.skinjectables.com/wp-content/uploads/2025/02/logo.png"
              alt="Skinjectables Logo"
              className="h-16 mx-auto mb-6"
            />
          </div>

          {/* Main Hero Content */}
          <div className="text-center mb-12">
            {/* Event Date Badge */}
            <div className="inline-flex items-center gap-3 bg-white/30 backdrop-blur-md rounded-full px-6 py-3 mb-6 border border-white/20 shadow-lg">
              <Calendar className="w-5 h-5 text-[#e2c1b6]" />
              <span className="text-[#4d3e3e] font-medium">January 21st</span>
              <Clock className="w-5 h-5 text-[#e2c1b6]" />
              <span className="text-[#4d3e3e] font-medium">10am - 2pm</span>
            </div>

            {/* Main Headlines */}
            <h1 className="text-5xl md:text-7xl font-bold text-[#4d3e3e] mb-4 leading-tight">
              DAXXIFY DAY
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#4d3e3e] mb-6">
              <span className="text-[#e2c1b6]">$6.50/unit</span> + 25% off ZO Products
            </h2>
            <p className="text-xl md:text-2xl text-[#4d3e3e] mb-4 font-medium">
              One Day Only - Book & Receive Treatment on 1/21
            </p>
            <p className="text-lg text-[#4d3e3e] mb-4 max-w-3xl mx-auto opacity-90">
              Join us for an exclusive event featuring Daxxify neurotoxin at special pricing, plus enjoy complimentary light bites while you get treated.
            </p>

            {/* Event Perks */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/40 rounded-full px-4 py-2">
                <Gift className="w-4 h-4 text-[#e2c1b6]" />
                <span className="text-[#4d3e3e] text-sm font-medium">25% Off ZO Skincare</span>
              </div>
              <div className="flex items-center gap-2 bg-white/40 rounded-full px-4 py-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-[#4d3e3e] text-sm font-medium">Complimentary Light Bites</span>
              </div>
              <div className="flex items-center gap-2 bg-white/40 rounded-full px-4 py-2">
                <Clock className="w-4 h-4 text-[#e2c1b6]" />
                <span className="text-[#4d3e3e] text-sm font-medium">One Day Only</span>
              </div>
            </div>

            {/* CTA Button */}
            <div className="flex flex-col items-center mb-8">
              <button
                onClick={() => setShowRSVPPopup(true)}
                className="bg-[#e2c1b6] hover:bg-[#d4b3a5] text-white font-bold py-4 px-10 rounded-full text-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                data-section="hero"
                data-event="hero_rsvp_click"
              >
                RSVP Today
              </button>
              <p className="text-sm text-[#4d3e3e] mt-3 opacity-80 max-w-md">
                Must receive treatment on January 21st between 10am-2pm to qualify for special pricing
              </p>
            </div>

            {/* Location Info */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-[#4d3e3e]">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#e2c1b6]" />
                <a
                  href="https://maps.app.goo.gl/YNtrupXBjuWmeKjh8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline"
                  data-section="hero"
                  data-event="hero_map_google_click"
                >
                  4330 N Campbell Ave #234, Tucson, AZ
                </a>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="font-medium">Free Parking Available</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RSVPPopup
        isOpen={showRSVPPopup}
        onClose={() => setShowRSVPPopup(false)}
      />
    </>
  );
};

export default HeroSection;